import { useState } from 'react';
import axios from 'axios';
import { ShieldCheck, Loader2, CheckCircle2, XCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function OpsSetupPage() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { success, message, action }
  const [error, setError] = useState('');
  const [form, setForm] = useState({ setup_token: '', email: '', password: '', name: 'Admin' });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const runSetup = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/ops/setup`, {
        setup_token: form.setup_token.trim(),
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim() || 'Admin',
      });
      setResult(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Setup failed — check server logs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: '#0f172a' }}
    >
      <div style={{ width: '100%', maxWidth: '420px' }}>
        {/* Header */}
        <div className="text-center mb-8">
          <div
            className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ background: 'rgba(99,148,198,0.15)', border: '1px solid rgba(99,148,198,0.3)' }}
          >
            <ShieldCheck size={22} style={{ color: '#6994c6' }} />
          </div>
          <h1 className="text-xl font-semibold" style={{ color: '#f1f5f9', fontFamily: 'Space Grotesk' }}>
            milk /ops setup
          </h1>
          <p className="text-sm mt-1" style={{ color: '#64748b' }}>
            One-time production database setup
          </p>
        </div>

        {/* Card */}
        <div className="rounded-2xl p-6" style={{ background: '#1e293b', border: '1px solid #334155' }}>

          {/* What this does */}
          <div
            className="rounded-xl p-4 mb-5 text-sm"
            style={{ background: 'rgba(99,148,198,0.08)', border: '1px solid rgba(99,148,198,0.2)', color: '#94a3b8' }}
          >
            <p className="font-medium mb-1" style={{ color: '#6994c6' }}>First-run only:</p>
            <ul className="space-y-1 text-xs">
              <li>• Creates the initial ops super_admin for this database</li>
              <li>• Requires the server-side setup token (see backend .env)</li>
              <li>• Locks permanently once an admin exists — resets use the CLI script</li>
            </ul>
          </div>

          {!result && (
            <div className="space-y-3 mb-4">
              <input
                data-testid="ops-setup-token-input"
                type="password"
                placeholder="Setup token"
                value={form.setup_token}
                onChange={set('setup_token')}
                className="w-full h-11 rounded-xl px-3 text-sm outline-none"
                style={{ background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0' }}
              />
              <input
                data-testid="ops-setup-email-input"
                type="email"
                placeholder="Admin email"
                value={form.email}
                onChange={set('email')}
                className="w-full h-11 rounded-xl px-3 text-sm outline-none"
                style={{ background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0' }}
              />
              <input
                data-testid="ops-setup-password-input"
                type="password"
                placeholder="Admin password"
                value={form.password}
                onChange={set('password')}
                className="w-full h-11 rounded-xl px-3 text-sm outline-none"
                style={{ background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0' }}
              />
              <input
                data-testid="ops-setup-name-input"
                type="text"
                placeholder="Name (optional)"
                value={form.name}
                onChange={set('name')}
                className="w-full h-11 rounded-xl px-3 text-sm outline-none"
                style={{ background: '#0f172a', border: '1px solid #334155', color: '#e2e8f0' }}
              />
            </div>
          )}

          {/* Result */}
          {result && (
            <div
              className="rounded-xl p-4 mb-4 flex items-start gap-3"
              style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)' }}
            >
              <CheckCircle2 size={18} style={{ color: '#4ade80', flexShrink: 0, marginTop: 2 }} />
              <div>
                <p className="text-sm font-medium" style={{ color: '#4ade80' }}>
                  {result.action === 'created' ? 'Account created!' : 'Password updated!'}
                </p>
                <p className="text-xs mt-0.5" style={{ color: '#86efac' }}>{result.message}</p>
                <p className="text-xs mt-2" style={{ color: '#64748b' }}>
                  You can now login at{' '}
                  <a href="/ops/login" style={{ color: '#6994c6' }}>/ops/login</a>
                </p>
              </div>
            </div>
          )}

          {error && (
            <div
              className="rounded-xl p-4 mb-4 flex items-start gap-3"
              style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}
            >
              <XCircle size={18} style={{ color: '#f87171', flexShrink: 0, marginTop: 2 }} />
              <p className="text-sm" style={{ color: '#fca5a5' }}>{error}</p>
            </div>
          )}

          {/* Button */}
          {!result ? (
            <button
              onClick={runSetup}
              disabled={loading || !form.setup_token || !form.email || !form.password}
              data-testid="ops-setup-submit-button"
              className="w-full h-11 rounded-xl text-sm font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: '#6994c6' }}
            >
              {loading
                ? <><Loader2 size={16} className="animate-spin" />Running setup…</>
                : <><ShieldCheck size={16} />Run Production Setup</>}
            </button>
          ) : (
            <a
              href="/ops/login"
              className="w-full h-11 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
              style={{ background: '#6994c6', color: 'white' }}
            >
              Go to /ops login →
            </a>
          )}
        </div>

        <p className="text-center text-xs mt-4" style={{ color: '#334155' }}>
          This page is only useful once. After setup, the database entry persists.
        </p>
      </div>
    </div>
  );
}
